import React, { useState, useEffect } from 'react';
import { Copy, ExternalLink, TrendingUp, Users, Coins, Sparkles, Eye } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useNavigate } from 'react-router-dom';
import { blinksApi, getBaseUrl } from '../services/blinksApi';

interface Blink {
  id: string;
  name: string;
  link: string;
  createdAt: string;
  walletAddress?: string;
  totalPlays: number;
  totalEarnings: string;
  entryCost?: number; // Season 2: in tokens
  hasJackpot?: boolean;
  jackpotBase?: number;
  jackpotBalance?: number; // Current jackpot balance
}

interface CommunityCrate {
  id: string;
  name: string;
  creator: string;
  totalPlays: number;
  entryCost: string; // Display format (e.g., "250 $BLOOT")
  winRate?: string;
  trending: boolean;
  jackpotBalance?: number; // Current jackpot balance
  jackpotBase?: number; // Base jackpot amount
  hasJackpot?: boolean;
}

const Blinks: React.FC = () => {
  const { publicKey, connected } = useWallet();
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [userBlinks, setUserBlinks] = useState<Blink[]>([]);
  const [communityCrates, setCommunityCrates] = useState<CommunityCrate[]>([]);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isLoadingCommunity, setIsLoadingCommunity] = useState(true);

  // Load cases from database and verify on-chain
  useEffect(() => {
    const loadAndVerifyBlinks = async () => {
      try {
        setIsVerifying(true);
        
        // Load cases from database for the connected wallet
        let dbBlinks: Blink[] = [];
        if (publicKey) {
          try {
            const dbCases = await blinksApi.getCasesByCreator(publicKey.toString());
            const apiBaseUrl = getBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:3001');
            dbBlinks = dbCases.cases.map(c => ({
              id: c.crateId,
              name: c.name,
              link: `${apiBaseUrl}/api/blinks/action/${c.crateId}${publicKey ? `?affiliate=${publicKey.toString()}` : ''}`,
              createdAt: new Date(c.createdAt).toISOString().split('T')[0],
              walletAddress: c.creatorWallet,
              totalPlays: c.totalPlays,
              totalEarnings: `${c.totalEarnings.toFixed(2)} $BLOOT`,
              entryCost: c.entryCost,
              hasJackpot: c.hasJackpotTier,
              jackpotBase: c.jackpotBase,
            }));
          } catch (error) {
            console.error('Error loading cases from database:', error);
          }
        }
        
        // Verify each case exists on-chain and get updated data
        const verifiedBlinks: Blink[] = [];
        for (const blink of dbBlinks) {
          try {
            // Extract case ID from link
            let caseId: string;
            try {
              const linkUrl = new URL(blink.link);
              caseId = linkUrl.pathname.split('/').pop() || blink.id;
            } catch {
              // If link parsing fails, use the blink.id directly
              caseId = blink.id;
            }

            // Verify case exists on-chain and get updated data
            const exists = await blinksApi.verifyCaseExists(caseId);
            if (exists) {
              // Try to get updated info from on-chain
              try {
                const crateConfig = await blinksApi.getCrateConfig(caseId);
                if (crateConfig) {
                  // Update with on-chain data
                  verifiedBlinks.push({
                    ...blink,
                    entryCost: crateConfig.entryCost,
                    totalPlays: crateConfig.totalPlays,
                    totalEarnings: `${crateConfig.totalEarnings.toFixed(2)} $BLOOT`,
                    hasJackpot: crateConfig.hasJackpotTier,
                    jackpotBase: crateConfig.jackpotBase,
                    jackpotBalance: crateConfig.jackpotBalance,
                  });
                } else {
                  // Keep existing data if we can't fetch new data
                  verifiedBlinks.push(blink);
                }
              } catch (e) {
                // Still add it if verification passed, but keep existing data
                verifiedBlinks.push(blink);
              }
            } else {
              // Case doesn't exist on-chain, skip it
              console.log(`Skipping case ${caseId} - not found on-chain`);
            }
          } catch (e) {
            console.error(`Error verifying case ${blink.id}:`, e);
            // Skip invalid entries
          }
        }

        setUserBlinks(verifiedBlinks);
      } catch (error) {
        console.error('Failed to load blinks:', error);
        setUserBlinks([]);
      } finally {
        setIsVerifying(false);
      }
    };

    loadAndVerifyBlinks();
  }, [publicKey]);

  // Load all cases from database for community crates
  useEffect(() => {
    const loadAllCases = async () => {
      try {
        setIsLoadingCommunity(true);
        const { cases } = await blinksApi.getAllCases();
        
        // Filter out user's own cases (they're already in userBlinks)
        const userCaseIds = new Set(userBlinks.map(b => b.id));
        const communityCases = cases
          .filter(c => !userCaseIds.has(c.crateId))
          .map(c => ({
            id: c.crateId,
            name: c.name,
            creator: `${c.creatorWallet.slice(0, 5)}...${c.creatorWallet.slice(-3)}`,
            totalPlays: c.totalPlays,
            entryCost: `${c.entryCost.toLocaleString()} $BLOOT`,
            trending: c.totalPlays > 100, // Consider trending if > 100 plays
            hasJackpot: c.hasJackpotTier,
            jackpotBase: c.jackpotBase,
            jackpotBalance: c.jackpotBalance,
          }))
          .sort((a, b) => b.totalPlays - a.totalPlays); // Sort by total plays descending
        
        setCommunityCrates(communityCases);
      } catch (error) {
        console.error('Error loading all cases:', error);
        setCommunityCrates([]);
      } finally {
        setIsLoadingCommunity(false);
      }
    };

    loadAllCases();
  }, [userBlinks]);

  // Helper to get Blink link with affiliate parameter if wallet is connected
  const getBlinkLink = (blink: Blink): string => {
    try {
      const url = new URL(blink.link);
      // If wallet is connected, add/update affiliate parameter
      if (connected && publicKey) {
        url.searchParams.set('affiliate', publicKey.toString());
      }
      return url.toString();
    } catch {
      // If link parsing fails, return original link
      return blink.link;
    }
  };

  const handleCopy = (link: string, id: string) => {
    navigator.clipboard.writeText(link);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const navigateToPreview = (id: string) => {
    let url = `/preview/${id}`;
    if (connected && publicKey) {
      url += `?affiliate=${publicKey.toString()}`;
    }
    navigate(url);
  };

  return (
    <div className="blinks-container">
      {/* Your Blinks Section */}
      <div className="blinks-section">
        <div className="section-header">
          <h3>Your Blinks</h3>
          <p className="section-desc">View and track your created loot crates. Note: Crates cannot be paused or deleted once created.</p>
        </div>

        {isVerifying ? (
          <div className="empty-state">
            <Sparkles size={48} color="#888" />
            <p>Verifying cases on-chain...</p>
          </div>
        ) : userBlinks.length === 0 ? (
          <div className="empty-state">
            <Sparkles size={48} color="#888" />
            <p>You haven't created any blinks yet.</p>
            <p className="empty-hint">Create your first Blootbox to get started!</p>
          </div>
        ) : (
          <div className="blinks-grid">
            {userBlinks.map((blink) => (
              <div 
                key={blink.id} 
                className="blink-card"
                onClick={() => navigateToPreview(blink.id)}
                style={{ cursor: 'pointer' }}
              >
                <div className="blink-card-header">
                  <div>
                    <h4>{blink.name}</h4>
                  </div>
                </div>
                
                <div className="blink-stats">
                  <div className="stat-item">
                    <Users size={16} />
                    <span>{blink.totalPlays.toLocaleString()} plays</span>
                  </div>
                  <div className="stat-item">
                    <Coins size={16} />
                    <span>{blink.totalEarnings}</span>
                  </div>
                  {blink.entryCost && (
                    <div className="stat-item">
                      <span style={{ fontSize: '0.875rem', color: '#888' }}>Entry:</span>
                      <span style={{ color: '#00FF9C', fontWeight: 'bold' }}>
                        {blink.entryCost.toLocaleString()} $BLOOT
                      </span>
                    </div>
                  )}
                  {blink.hasJackpot && (blink.jackpotBase !== undefined || blink.jackpotBalance !== undefined) && (
                    <div className="stat-item" style={{ color: '#FFD700' }}>
                      🎰 Jackpot: {((blink.jackpotBalance || 0) + (blink.jackpotBase || 0)).toLocaleString()} $BLOOT
                    </div>
                  )}
                </div>

                <div className="blink-link-section" onClick={(e) => e.stopPropagation()}>
                  <div className="blink-link-display">
                    <span 
                      className="link-text" 
                      onClick={() => handleCopy(getBlinkLink(blink), blink.id)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to copy Blink"
                    >
                      {getBlinkLink(blink)}
                    </span>
                    <button
                      className="icon-btn"
                      onClick={() => handleCopy(getBlinkLink(blink), blink.id)}
                      title="Copy link"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="icon-btn"
                      onClick={() => navigateToPreview(blink.id)}
                      title="Preview Blink"
                    >
                      <Eye size={16} />
                    </button>
                    <a
                      href={getBlinkLink(blink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="icon-btn"
                      title="Open link"
                    >
                      <ExternalLink size={16} />
                    </a>
                  </div>
                  {copiedId === blink.id && (
                    <span className="copy-feedback">Copied!</span>
                  )}
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Crates Section */}
      <div className="blinks-section" style={{ marginTop: '64px' }}>
        <div className="section-header">
          <h3>All Crates</h3>
          <p className="section-desc">Explore trending loot crates from the community.</p>
        </div>

        {isLoadingCommunity ? (
          <div className="empty-state">
            <Sparkles size={48} color="#888" />
            <p>Loading crates...</p>
          </div>
        ) : communityCrates.length === 0 ? (
          <div className="empty-state">
            <Sparkles size={48} color="#888" />
            <p>No community crates found.</p>
          </div>
        ) : (
          <div className="blinks-grid">
            {communityCrates.map((crate) => {
              const apiBaseUrl = getBaseUrl(process.env.REACT_APP_API_URL || 'http://localhost:3001');
              const blinkLink = `${apiBaseUrl}/api/blinks/action/${crate.id}${connected && publicKey ? `?affiliate=${publicKey.toString()}` : ''}`;
              
              return (
                <div 
                  key={crate.id} 
                  className="blink-card"
                  onClick={() => navigateToPreview(crate.id)}
                  style={{ cursor: 'pointer' }}
                >
                  {crate.trending && (
                    <div className="trending-badge" style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
                      <TrendingUp size={14} />
                      <span>TRENDING</span>
                    </div>
                  )}
                  
                  <div className="blink-card-header">
                    <div>
                      <h4>{crate.name}</h4>
                    </div>
                  </div>
                  
                  <div className="blink-stats">
                    <div className="stat-item">
                      <Users size={16} />
                      <span>{crate.totalPlays.toLocaleString()} plays</span>
                    </div>
                    <div className="stat-item">
                      <span style={{ fontSize: '0.875rem', color: '#888' }}>Creator:</span>
                      <span style={{ fontSize: '0.875rem' }}>{crate.creator}</span>
                    </div>
                    <div className="stat-item">
                      <span style={{ fontSize: '0.875rem', color: '#888' }}>Entry:</span>
                      <span style={{ color: '#00FF9C', fontWeight: 'bold' }}>
                        {crate.entryCost}
                      </span>
                    </div>
                    {crate.hasJackpot && (crate.jackpotBalance !== undefined || crate.jackpotBase) && (
                      <div className="stat-item" style={{ color: '#FFD700' }}>
                        🎰 Jackpot: {((crate.jackpotBalance || 0) + (crate.jackpotBase || 0)).toLocaleString()} $BLOOT
                      </div>
                    )}
                  </div>

                  <div className="blink-link-section" onClick={(e) => e.stopPropagation()}>
                    <div className="blink-link-display">
                      <span 
                        className="link-text" 
                        onClick={() => handleCopy(blinkLink, crate.id)}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title="Click to copy Blink"
                      >
                        {blinkLink}
                      </span>
                      <button
                        className="icon-btn"
                        onClick={() => handleCopy(blinkLink, crate.id)}
                        title="Copy link"
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => navigateToPreview(crate.id)}
                        title="Preview Blink"
                      >
                        <Eye size={16} />
                      </button>
                      <a
                        href={blinkLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="icon-btn"
                        title="Open link"
                      >
                        <ExternalLink size={16} />
                      </a>
                    </div>
                    {copiedId === crate.id && (
                      <span className="copy-feedback">Copied!</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Blinks;
